import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  translationService,
  type Translation,
  type TranslationCreateInput,
  type TranslationUpdateInput,
} from '../services/translations.service';
import { toast } from '../utils/toast';

// Query keys
export const translationKeys = {
  all: ['translations'] as const,
  lists: () => [...translationKeys.all, 'list'] as const,
  list: (filters?: { page?: string; section?: string; search?: string }) =>
    [...translationKeys.lists(), filters] as const,
  byPage: (page: string) => [...translationKeys.all, 'page', page] as const,
  details: () => [...translationKeys.all, 'detail'] as const,
  detail: (id: string) => [...translationKeys.details(), id] as const,
  stats: () => [...translationKeys.all, 'stats'] as const,
};

/**
 * Hook to get all translations with optional filters
 */
export function useTranslations(filters?: {
  page?: string;
  section?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: translationKeys.list(filters),
    queryFn: () => translationService.getAll(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to get translations for a specific page
 */
export function useTranslationsByPage(page: string) {
  return useQuery({
    queryKey: translationKeys.byPage(page),
    queryFn: () => translationService.getByPage(page),
    enabled: !!page,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to get single translation by ID
 */
export function useTranslation(id: string) {
  return useQuery({
    queryKey: translationKeys.detail(id),
    queryFn: () => translationService.getById(id),
    enabled: !!id,
  });
}

/**
 * Hook to get translation statistics
 */
export function useTranslationStats() {
  return useQuery({
    queryKey: translationKeys.stats(),
    queryFn: () => translationService.getStats(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to create a new translation
 */
export function useCreateTranslation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TranslationCreateInput) =>
      translationService.create(data),
    onSuccess: () => {
      // Invalidate all translation queries
      queryClient.invalidateQueries({ queryKey: translationKeys.all });

      toast.success('Translation created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create translation: ${error.message}`);
    },
  });
}

/**
 * Hook to update an existing translation
 */
export function useUpdateTranslation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TranslationUpdateInput }) =>
      translationService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches for all translation queries
      await queryClient.cancelQueries({ queryKey: translationKeys.all });

      // Snapshot previous values for rollback
      const previousDetail = queryClient.getQueryData<Translation>(
        translationKeys.detail(id)
      );

      // Get all page queries to update optimistically
      const queryCache = queryClient.getQueryCache();
      const pageQueries = queryCache.findAll({
        queryKey: translationKeys.all,
      });

      // Store previous list data for rollback
      const previousLists: {
        queryKey: readonly unknown[];
        data: Translation[];
      }[] = [];

      // Optimistically update detail query
      if (previousDetail) {
        queryClient.setQueryData<Translation>(translationKeys.detail(id), {
          ...previousDetail,
          ...data,
          updatedAt: new Date().toISOString(),
        });
      }

      // Optimistically update all list queries containing this translation
      pageQueries.forEach((query) => {
        const listData = query.state.data as Translation[] | undefined;
        if (Array.isArray(listData)) {
          const translationIndex = listData.findIndex((t) => t.id === id);
          if (translationIndex !== -1) {
            previousLists.push({
              queryKey: query.queryKey,
              data: listData,
            });

            const updatedList = [...listData];
            updatedList[translationIndex] = {
              ...updatedList[translationIndex],
              ...data,
              updatedAt: new Date().toISOString(),
            };
            queryClient.setQueryData(query.queryKey, updatedList);
          }
        }
      });

      return { previousDetail, previousLists };
    },
    onError: (error: Error, { id }, context) => {
      // Rollback detail query
      if (context?.previousDetail) {
        queryClient.setQueryData(
          translationKeys.detail(id),
          context.previousDetail
        );
      }

      // Rollback all list queries
      context?.previousLists.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data);
      });

      toast.error(`Failed to update translation: ${error.message}`);
    },
    onSuccess: (updatedTranslation) => {
      // Update with server response to ensure data consistency
      queryClient.setQueryData(
        translationKeys.detail(updatedTranslation.id),
        updatedTranslation
      );

      // Invalidate stats as they might have changed
      queryClient.invalidateQueries({ queryKey: translationKeys.stats() });

      toast.success('Translation updated successfully');
    },
  });
}

/**
 * Hook to delete a translation
 */
export function useDeleteTranslation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => translationService.delete(id),
    onSuccess: (_data, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: translationKeys.detail(id) });

      // Invalidate all lists
      queryClient.invalidateQueries({ queryKey: translationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: translationKeys.stats() });

      toast.success('Translation deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete translation: ${error.message}`);
    },
  });
}

/**
 * Hook to bulk upsert translations
 */
export function useBulkUpsertTranslations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (translations: TranslationCreateInput[]) =>
      translationService.bulkUpsert(translations),
    onSuccess: (result) => {
      // Invalidate all translation queries
      queryClient.invalidateQueries({ queryKey: translationKeys.all });

      toast.success(
        `Bulk operation completed: ${result.created} created, ${result.updated} updated`
      );
    },
    onError: (error: Error) => {
      toast.error(`Bulk operation failed: ${error.message}`);
    },
  });
}
